import { useEffect, useRef, type ReactNode } from 'react';
import { useLayer } from '../lib/layers.ts';
import { Icon } from './Icon.tsx';

// Vel vanaf de onderkant (telefoon): greepje, titel met sluitknop, inhoud die schuift en een
// veilige rand onderin. Esc, de achtergrond en de terugbeweging van iOS sluiten het. `auto` neemt
// de hoogte van de inhoud (tot 94 %), `full` altijd 94 %.

/** Open vellen, onderste eerst: Esc sluit alleen het bovenste (alle luisteraars zitten op document). */
const openSheets: symbol[] = [];

export function Sheet(props: { title: string; onClose: () => void; children: ReactNode; height?: 'auto' | 'full' | undefined; footer?: ReactNode | undefined; label?: string | undefined }) {
  const onClose = useRef(props.onClose);
  const token = useRef(Symbol('sheet'));
  useEffect(() => {
    onClose.current = props.onClose;
  });
  useLayer(true, () => onClose.current());
  useEffect(() => {
    const me = token.current;
    openSheets.push(me);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || openSheets[openSheets.length - 1] !== me) return;
      e.stopPropagation();
      onClose.current();
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      const i = openSheets.indexOf(me);
      if (i >= 0) openSheets.splice(i, 1);
    };
  }, []);
  return (
    <div className="fixed inset-0 z-40 flex flex-col justify-end bg-ink/40" onClick={(e) => e.target === e.currentTarget && props.onClose()}>
      <div role="dialog" aria-modal="true" aria-label={props.label ?? props.title} className={`sheet-in w-full flex flex-col bg-surface rounded-t-2xl shadow-card pb-safe ${props.height === 'full' ? 'h-[94%]' : 'max-h-[94%]'}`}>
        <div className="relative shrink-0 flex items-center gap-2 pl-5 pr-2 h-14 border-b border-line">
          <span className="absolute left-1/2 top-1.5 -translate-x-1/2 h-1 w-10 rounded-full bg-line-strong" aria-hidden="true" />
          <h2 className="font-display text-lg flex-1 truncate">{props.title}</h2>
          <button type="button" aria-label="Sluiten" className="h-11 w-11 rounded-md flex items-center justify-center text-ink-soft active:bg-tint" onClick={props.onClose}>
            <Icon name="x" size={20} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-auto overscroll-contain">{props.children}</div>
        {props.footer ? <div className="shrink-0 px-4 py-3 border-t border-line flex items-center justify-end gap-2">{props.footer}</div> : null}
      </div>
    </div>
  );
}
